import type { InstrumentId } from "../score";

export const DURATION = 18;
export const LEAD_IN = 0.1;
export const FINAL_BEAT = 48;
export const TEMPO = { start: 144, end: 216 } as const;
const slope = (TEMPO.end - TEMPO.start) / FINAL_BEAT;
/** BPM is linear in score beats. Integrate 60 / BPM, never stretch a recording. */
export const beatTime = (beat: number) =>
  LEAD_IN + (60 / slope) * Math.log1p((slope * beat) / TEMPO.start);
export const timeBeat = (time: number) =>
  (TEMPO.start / slope) * Math.expm1(((time - LEAD_IN) * slope) / 60);
export const bpmAt = (beat: number) => TEMPO.start + slope * beat;
export const CLIMAX = beatTime(FINAL_BEAT);
export const ENTRIES: Record<InstrumentId, number> = {
  marimba: 0,
  bell: 4,
  bass: 8,
  percussion: 12,
};
export type Note = Readonly<{
  id: string;
  instrumentId: InstrumentId;
  startBeat: number;
  durationBeats: number;
  midi: number;
  velocity: number;
  articulation: "normal" | "wood" | "shaker";
  time: number;
  end: number;
  duration: number;
  finale: boolean;
}>;
/** Inspected PD orchestral theme: B minor -> A minor, raised into mallet register.
 * MIDI preserves D# and Bb in bar 2. See docs/ORBIT_MUSIC_BOX_V2.md. */
export const THEME: readonly (readonly [number, number])[] = [
  [57, 0.5],
  [59, 0.5],
  [60, 0.5],
  [62, 0.5],
  [64, 0.5],
  [60, 0.5],
  [64, 1],
  [63, 0.5],
  [59, 0.5],
  [63, 1],
  [62, 0.5],
  [58, 0.5],
  [62, 1],
  [57, 0.5],
  [59, 0.5],
  [60, 0.5],
  [62, 0.5],
  [64, 0.5],
  [60, 0.5],
  [64, 0.5],
  [69, 0.5],
  [67, 0.5],
  [64, 0.5],
  [60, 0.5],
  [64, 0.5],
  [67, 2],
];
const notes: Note[] = [];
const add = (
  instrumentId: InstrumentId,
  startBeat: number,
  midi: number,
  durationBeats: number,
  velocity: number,
  articulation: Note["articulation"] = "normal",
  finale = false,
) => {
  const time = beatTime(startBeat),
    end = beatTime(startBeat + durationBeats);
  notes.push({
    id: `v2-${instrumentId}-${notes.length}`,
    instrumentId,
    startBeat,
    midi,
    durationBeats,
    velocity,
    articulation,
    time,
    end,
    duration: end - time,
    finale,
  });
};
// Three recognisable statements. The last two beats turn to E major, then A minor.
for (const offset of [0, 16, 32]) {
  let beat = offset;
  for (const [pitch, length] of THEME) {
    const midi = beat >= 46 ? 64 : pitch + (offset === 32 ? 12 : 0);
    add(
      "marimba",
      beat,
      midi,
      Math.min(length * 0.72, 0.8),
      0.65 + (beat / 48) * 0.28 + (beat % 2 === 0 ? 0.05 : 0),
    );
    beat += length;
  }
}
// Bell answers off the beat, then approaches the lead's attacks during the chase.
for (let beat = 4; beat < 48; beat += beat < 24 ? 2 : 1) {
  const lag = beat < 40 ? 0.25 : (0.25 * (48 - beat)) / 8;
  const pitch =
    beat >= 44
      ? [76, 80, 83, 80][beat % 4]
      : [69, 72, 76, 72][Math.floor(beat / 2) % 4] +
        (beat >= 16 && beat < 32 ? 12 : 0);
  add("bell", beat + lag, pitch, 0.6, 0.43 + (beat / 48) * 0.4);
}
// Pedal / dominant bass, with increasingly close attacks on the final approach.
for (let beat = 8; beat < 48; beat += beat < 32 ? 2 : 1) {
  const lag = beat === 8 ? 0 : beat < 40 ? 0.5 : (0.5 * (48 - beat)) / 8;
  add(
    "bass",
    beat + lag,
    beat >= 44 ? 40 : beat % 4 === 0 ? 45 : 40,
    beat < 32 ? 1.15 : 0.55,
    0.56 + (beat / 48) * 0.38,
  );
}
for (let beat = 12; beat < 48; beat += beat < 24 ? 0.5 : 0.25) {
  const lag = beat === 12 ? 0 : beat < 40 ? 0.125 : (0.125 * (48 - beat)) / 8;
  add(
    "percussion",
    beat + lag,
    76,
    0.1,
    beat % 1 === 0 ? 0.48 + (beat / 48) * 0.42 : 0.24 + (beat / 48) * 0.18,
    beat % 1 === 0 ? "wood" : "shaker",
  );
}
// One shared score instant for all four rings, PCM attacks and icon choreography.
for (const [instrument, midi] of [
  ["marimba", 69],
  ["bell", 84],
  ["bass", 45],
  ["percussion", 76],
] as const)
  add(
    instrument,
    FINAL_BEAT,
    midi,
    instrument === "percussion" ? 0.16 : 1.8,
    1,
    instrument === "percussion" ? "wood" : "normal",
    true,
  );
export const EVENTS: readonly Note[] = notes.sort(
  (a, b) => a.time - b.time || a.id.localeCompare(b.id),
);
export const PARTS: Record<InstrumentId, readonly Note[]> = {
  marimba: EVENTS.filter((e) => e.instrumentId === "marimba"),
  bell: EVENTS.filter((e) => e.instrumentId === "bell"),
  bass: EVENTS.filter((e) => e.instrumentId === "bass"),
  percussion: EVENTS.filter((e) => e.instrumentId === "percussion"),
};
